/* eslint-disable @typescript-eslint/no-implied-eval */

'use client'
import { TemplateContext, TemplateEngine } from '@/lib/templateengine/client'
import React, { useEffect, useMemo, useRef } from 'react'

interface ShowHtmlElementProps {
  html: string
  css?: string
  js?: string
  context: TemplateContext
  extractStylesFromHtml?: boolean
  removeHtmlTags?: boolean
}

export const FulmoTemplateEngine: React.FC<ShowHtmlElementProps> = ({
  html,
  css = '',
  js = '',
  context,
  extractStylesFromHtml = true,
  removeHtmlTags = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const { extractedCss, cleanedHtml } = useMemo(() => {
    const engine = new TemplateEngine(context)
    const processedHtml = engine.render(html)

    if (!extractStylesFromHtml && !removeHtmlTags) {
      return {
        extractedCss: '',
        cleanedHtml: processedHtml,
      }
    }

    let extractedStyles = ''
    let cleaned = processedHtml

    if (extractStylesFromHtml) {
      // <style>タグを全て抽出
      const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi
      const styleMatches = cleaned.match(styleRegex)

      if (styleMatches) {
        styleMatches.forEach((styleTag) => {
          // <style>タグの中身を取り出す
          const styleContent = styleTag.replace(/<style[^>]*>|<\/style>/gi, '')
          extractedStyles += styleContent + '\n'
        })

        // <style>タグを削除
        cleaned = cleaned.replace(styleRegex, '')
      }
    }

    if (removeHtmlTags) {
      cleaned = cleaned.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')

      const bodyRegex = /<body[^>]*>([\s\S]*?)<\/body>/i
      const bodyMatch = cleaned.match(bodyRegex)
      if (bodyMatch) {
        cleaned = bodyMatch[1]
      }

      cleaned = cleaned.replace(/<\/?html[^>]*>/gi, '')
      cleaned = cleaned.replace(/<\/?body[^>]*>/gi, '')
    }

    return {
      extractedCss: extractedStyles.trim(),
      cleanedHtml: cleaned.trim(),
    }
  }, [html, context, extractStylesFromHtml, removeHtmlTags])

  const finalCss = useMemo(() => {
    const parts: string[] = []

    // HTML内から抽出したCSS
    if (extractedCss) {
      parts.push(extractedCss)
    }

    // 外部から渡されたCSS
    if (css) {
      const engine = new TemplateEngine(context)
      parts.push(engine.render(css))
    }

    return parts.join('\n\n')
  }, [extractedCss, css, context])

  useEffect(() => {
    if (!js || !containerRef.current) return

    try {
      const engine = new TemplateEngine(context)
      const renderedJs = engine.render(js)

      const safeContext = {
        ...context,
        element: containerRef.current,
        querySelector: (selector: string) =>
          containerRef.current?.querySelector(selector),
        querySelectorAll: (selector: string) =>
          containerRef.current?.querySelectorAll(selector),
        console: {
          log: console.log,
          error: console.error,
          warn: console.warn,
        },
      }

      const fn = new Function(
        'context',
        `
        'use strict';
        with (context) {
          ${renderedJs}
        }
        `,
      )
      fn(safeContext)
    } catch (e) {
      console.error('Script execution error:', e)
    }
  }, [js, context])

  return (
    <div ref={containerRef}>
      {finalCss && <style>{finalCss}</style>}
      <div dangerouslySetInnerHTML={{ __html: cleanedHtml }} />
    </div>
  )
}
